/**
 * GENERATED — do not edit.
 *
 * Source: `semantic.css`. Regenerate with `pnpm --filter @aegis/ui gen`.
 * `pnpm verify` fails if this file is stale, so the CSS and the TypeScript
 * cannot disagree about what colour a thing is.
 *
 * These are the tokens shared across web, desktop and mobile. Metric tokens
 * (spacing, type scale, radius, motion) are deliberately NOT here — they are
 * per-platform. See docs/18 §7.
 *
 * Token names are the CSS custom property without the leading `--`, camelCased:
 * `--market-up` is `marketUp`.
 */

export const dark = {
  surfaceBase: '#0B0F15',
  surfaceRaised: '#111720',
  surfaceOverlay: '#18202B',
  surfaceSunken: '#070A0E',
  surfaceHover: '#1F2937',
  lineSubtle: '#141B24',
  lineDefault: '#202A36',
  lineStrong: '#33404F',
  edgeHighlight: 'rgba(255, 255, 255, 0.04)',
  textPrimary: '#F2EEE5',
  textSecondary: '#A6ADB8',
  textMuted: '#878F9B',
  textFaint: '#596573',
  textInverse: '#0B0F15',
  marketUp: '#4FA383',
  marketUpSoft: 'rgba(79, 163, 131, 0.14)',
  marketDown: '#CE5C55',
  marketDownSoft: 'rgba(204, 86, 79, 0.14)',
  marketFlat: '#76808D',
  aiPrimary: '#8E7BE0',
  aiSoft: 'rgba(142, 123, 224, 0.13)',
  aiBorder: 'rgba(142, 123, 224, 0.38)',
  aiText: '#B9ABF2',
  userDrawing: '#F2EEE5',
  userDrawingSoft: 'rgba(242, 238, 229, 0.10)',
  statusOk: '#4FA383',
  statusCaution: '#C9922F',
  statusWarning: '#D2703A',
  statusBreached: '#CC564F',
  statusOkSoft: 'rgba(79, 163, 131, 0.13)',
  statusCautionSoft: 'rgba(201, 146, 47, 0.13)',
  statusWarningSoft: 'rgba(210, 112, 58, 0.13)',
  statusBreachedSoft: 'rgba(204, 86, 79, 0.13)',
  brand: '#C08A3E',
  brandSoft: 'rgba(192, 138, 62, 0.15)',
  brandStrong: '#D9A254',
  accent: '#5B87D6',
  accentSoft: 'rgba(91, 135, 214, 0.14)',
  accentHi: '#608cdc',
  accentLo: '#5682d0',
  controlHi: '#232d3b',
  controlLo: '#1b2533',
} as const;

/** Fully resolved — authored in CSS as a partial override, flattened here. */
export const light: Record<SemanticToken, string> = {
  surfaceBase: '#FBFAF7',
  surfaceRaised: '#F4F2ED',
  surfaceOverlay: '#EDEAE3',
  surfaceSunken: '#E5E1D8',
  surfaceHover: '#E3DFD5',
  lineSubtle: '#EBE7DE',
  lineDefault: '#DAD5C9',
  lineStrong: '#B9B2A2',
  edgeHighlight: 'rgba(255, 255, 255, 0.7)',
  textPrimary: '#1A1F26',
  textSecondary: '#4A5460',
  textMuted: '#5C646E',
  textFaint: '#838D9A',
  textInverse: '#FBFAF7',
  marketUp: '#2F7A5F',
  marketUpSoft: 'rgba(47, 122, 95, 0.12)',
  marketDown: '#A83A34',
  marketDownSoft: 'rgba(168, 58, 52, 0.12)',
  marketFlat: '#76808D',
  aiPrimary: '#5B3FA8',
  aiSoft: 'rgba(91, 63, 168, 0.10)',
  aiBorder: 'rgba(91, 63, 168, 0.32)',
  aiText: '#4A3090',
  userDrawing: '#1A1F26',
  userDrawingSoft: 'rgba(26, 31, 38, 0.08)',
  statusOk: '#2F7A5F',
  statusCaution: '#8A6410',
  statusWarning: '#A2521C',
  statusBreached: '#A83A34',
  statusOkSoft: 'rgba(47, 122, 95, 0.12)',
  statusCautionSoft: 'rgba(138, 100, 16, 0.12)',
  statusWarningSoft: 'rgba(162, 82, 28, 0.12)',
  statusBreachedSoft: 'rgba(168, 58, 52, 0.12)',
  brand: '#8A5A18',
  brandSoft: 'rgba(138, 90, 24, 0.12)',
  brandStrong: '#6E460F',
  accent: '#2C5AA8',
  accentSoft: 'rgba(44, 90, 168, 0.12)',
  accentHi: '#315fae',
  accentLo: '#2755a3',
  controlHi: '#e8e4da',
  controlLo: '#dedad0',
};

export type SemanticToken = keyof typeof dark;
export type Palette = Record<SemanticToken, string>;

/** The palette for a theme. The only sanctioned way for native code to get a colour. */
export function palette(theme: 'dark' | 'light'): Palette {
  return theme === 'light' ? light : dark;
}

/** The CSS custom property name for a token, for canvas code that reads the DOM. */
export function cssVar(name: SemanticToken): string {
  return '--' + name.replace(/[A-Z0-9]/g, (c) => '-' + c.toLowerCase());
}
